import { ReactNode, useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase/client';
import { Alert, Spinner } from '../../components/common';

interface RoleGuardProps {
  children: ReactNode;
  roles: string[]; // Array of allowed roles
  fallback?: ReactNode; // Optional fallback component to render instead of redirecting
}

export const RoleGuard = ({ children, roles, fallback }: RoleGuardProps) => {
  const { teamId } = useParams<{ teamId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teamId) {
      setIsLoading(false);
      setHasAccess(false);
      return;
    }

    const checkRole = async () => {
      try {
        setIsLoading(true);
        
        if (!supabase) {
          console.error('Supabase client not initialized');
          setHasAccess(false);
          setIsLoading(false);
          return;
        }
        
        // Get current user
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
          setHasAccess(false);
          setIsLoading(false);
          return;
        }
        
        // Check user's role in this team
        const { data: member, error: memberError } = await supabase
          .from('team_members')
          .select('role')
          .eq('team_id', teamId)
          .eq('user_id', userData.user.id)
          .single();
        
        if (memberError) {
          console.error('Error checking team role:', memberError);
          setHasAccess(false);
          setIsLoading(false);
          return;
        }
        
        // Check if user's role is in the allowed roles
        const hasRole = member && roles.includes(member.role);
        setHasAccess(!!hasRole);
        setIsLoading(false);
      } catch (err) {
        console.error('Error in RoleGuard:', err);
        setError(err instanceof Error ? err.message : 'Failed to check role access');
        setIsLoading(false);
        setHasAccess(false);
      }
    };
    
    checkRole();
  }, [teamId, roles]);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Spinner size="lg" />
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="error" className="my-4">
        {error}
      </Alert>
    );
  }
  
  if (!hasAccess) {
    // If fallback is provided, render it
    if (fallback) {
      return <>{fallback}</>;
    }
    
    // Otherwise redirect to the team dashboard
    return teamId ? <Navigate to={`/teams/${teamId}/dashboard`} replace /> : <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}; 